import { AfterViewInit, Component, ElementRef, HostListener, ViewChild, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { PropertiesService } from '../../core/services/properties.service';
import { Property } from '../../core/models/property.model';
import { LoadingSpinnerComponent } from '../../shared/components/loading-spinner/loading-spinner.component';
import { LucideIconsModule } from '../../shared/lucide-icons.module';
import { PropertyCardComponent } from '../../shared/components/property-card/property-card.component';
import { CurrencyService } from '../../core/services/currency.service';
import { CountrySelectorComponent } from '../../shared/components/country-selector/country-selector.component';
import { GeoService } from '../../core/services/geo.service';
import { PROPERTY_TYPE_OPTIONS, PropertyTypeValue } from '../../shared/data/property-types';
import { ActivatedRoute } from '@angular/router';
import { ProjectsService } from '../../core/services/projects.service';
import { Project } from '../../core/models/project.model';
import { NotificationService } from '../../services/notification.service';
import { propertyFeatureLabels, propertyFeatureSearchTerms, propertyHasGarden, propertyHasTerrace } from '../../shared/utils/property-features.util';
import { propertyStatusKey } from '../../shared/utils/property-status.util';
import { ReservationsService } from '../../core/services/reservations.service';
import { Store } from '@ngrx/store';
import { selectIsAuthenticated } from '../../store/auth/auth.selectors';
import { StableStickyFilterDirective } from '../../shared/directives/stable-sticky-filter.directive';

type SortKey = 'nearest' | 'recent' | 'price-low' | 'price-high' | 'ppsqm-low' | 'ppsqm-high' | 'bedrooms' | 'bathrooms' | 'title' | 'unit-type';

@Component({
    selector: 'app-properties-page',
    standalone: true,
    imports: [CommonModule, FormsModule, LoadingSpinnerComponent, LucideIconsModule, PropertyCardComponent, CountrySelectorComponent, StableStickyFilterDirective],
    templateUrl: './properties-page.component.html',
    styleUrls: ['./properties-page.component.scss'],
})
export class PropertiesPageComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('filterSentinel', { static: true }) private filterSentinelRef!: ElementRef<HTMLElement>;

    private propertiesService = inject(PropertiesService);
    private projectsService = inject(ProjectsService);
    private reservationsService = inject(ReservationsService);
    private store = inject(Store);
    currencyService = inject(CurrencyService);
    private geoService = inject(GeoService);
    private route = inject(ActivatedRoute);
    private notifications = inject(NotificationService);
    private currencySub: ReturnType<typeof this.currencyService.currencyChanges$.subscribe> | null = null;
    private reservedPropertyIds = new Set<string>();
    properties: Property[] = [];
    filteredProperties: Property[] = [];
    availableTags: string[] = [];
    readonly propertyTypeOptions = PROPERTY_TYPE_OPTIONS;
    bedroomOptions = [1, 2, 3, 4, 5, 6];
    bathroomOptions = [1, 2, 3, 4, 5, 6];
    totalCount = 0;
    loading = true;
    loadingPage = false;
    currentPage = 1;
    pageSize = 10;
    hasNextPage = true;
    filterTab: 'general' | 'location' = 'general';
    projectsForFilter: Project[] = [];
    projectsLoading = false;
    projectsError: string | null = null;
    private projectsLoaded = false;
    private projectsLoadPromise: Promise<void> | null = null;

    filters: {
        minPrice?: number | null;
        maxPrice?: number | null;
        bedrooms?: number | null;
        bathrooms?: number | null;
        status: string;
        country: string;
        location: string;
        projectId: string;
        propertyType: PropertyTypeValue;
        featureGarden: boolean;
        featureTerrace: boolean;
        featureGroundFloor: boolean;
        search: string;
        sortBy: SortKey;
        tags: string[];
    } = {
            status: 'any',
            country: '',
            location: '',
            projectId: '',
            propertyType: 'any',
            featureGarden: false,
            featureTerrace: false,
            featureGroundFloor: false,
            search: '',
            sortBy: 'recent',
            tags: [],
            bedrooms: null,
            bathrooms: null,
            minPrice: null,
            maxPrice: null
        };
    showFilters = false;
    private tagSet = new Set<string>();
    private userCountryCode = '';
    private initialLoad = true;
    private initialQueryHasFilters = false;
    private notifiedNoPropertiesInCountry = false;
    private userGeo: { lat: number; lng: number } | null = null;
    private geoPromise: Promise<void> | null = null;
    private lastRequestHadGeo = false;
    private lastRequestSortBy: SortKey = 'recent';
    isFilterStuck = false;
    private isFilterCollapsed = false;
    private viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
    private bodyScrollLocked = false;
    stickyTopOffsetPx = 0;
    private stickyFrameId: number | null = null;
    private stickyTriggerNeedsUpdate = false;
    private stickyTriggerScrollY: number | null = null;
    private stickyTopOffsetElement: HTMLElement | null = null;

    get isMobileViewport(): boolean {
        return this.viewportWidth < 1024;
    }

    get isStickyCompact(): boolean {
        if (this.showFilters) return false;
        return this.isMobileViewport ? this.isFilterStuck : this.isFilterCollapsed;
    }

    @HostListener('window:scroll')
    onWindowScroll() {
        this.queueStickyUpdate();
    }

    private queueStickyUpdate(remeasureTrigger = false): void {
        if (typeof window === 'undefined') return;
        this.stickyTriggerNeedsUpdate ||= remeasureTrigger;
        if (this.stickyFrameId !== null) return;
        this.stickyFrameId = window.requestAnimationFrame(() => {
            this.stickyFrameId = null;
            this.updateStickyTopOffset();
            if (this.stickyTriggerNeedsUpdate) this.updateStickyTriggerPosition();
            this.stickyTriggerNeedsUpdate = false;
            this.updateFilterStuckState();
            this.updateFilterCollapsedState();
        });
    }

    private normalizeVisibleTotalCount(reportedTotal: number, loadedCount: number, hasNextPage: boolean): number {
        const safeLoaded = Math.max(0, Number(loadedCount) || 0);
        const safeReported = Number.isFinite(reportedTotal) ? Math.max(0, Number(reportedTotal)) : safeLoaded;
        if (hasNextPage) return Math.max(safeReported, safeLoaded);
        return safeLoaded || safeReported;
    }

    private hasClientOnlyFiltersActive(): boolean {
        return Boolean(
            (Number.isFinite(this.filters.minPrice as number) && (this.filters.minPrice as number) !== null)
            || (Number.isFinite(this.filters.maxPrice as number) && (this.filters.maxPrice as number) !== null)
            || this.filters.featureGarden
            || this.filters.featureTerrace
            || this.filters.featureGroundFloor
            || this.filters.tags.length
        );
    }

    @HostListener('window:resize')
    onWindowResize() {
        this.viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
        this.updateStickyTopOffset();
        this.queueStickyUpdate(true);
        this.syncBodyScrollLock();
    }

	    ngOnInit() {
	        this.onWindowResize();
	        this.applyQueryParams();
	        this.syncBodyScrollLock();
	        this.currencySub = this.currencyService.currencyChanges$.subscribe(() => this.applyLocalFilters(this.properties));
	        this.applyCachedGeo();
	        this.fetchPage(); // single Public_API/properties call on load
	        void this.loadUserGeoContext(); // best-effort (cached) for country boost
            void this.maybeNotifyNoPropertiesInCountry();
            void this.loadReservedPropertyIds();
	    }

    ngAfterViewInit(): void {
        this.queueStickyUpdate(true);
    }

    ngOnDestroy(): void {
        if (this.stickyFrameId !== null) window.cancelAnimationFrame(this.stickyFrameId);
        this.currencySub?.unsubscribe();
        this.setBodyScrollLocked(false);
    }

    toggleFilters() {
        this.showFilters = !this.showFilters;
        if (this.showFilters) this.isFilterCollapsed = false;
        this.syncBodyScrollLock();
        this.queueStickyUpdate(true);
    }

    closeFilters() {
        this.showFilters = false;
        this.syncBodyScrollLock();
        this.queueStickyUpdate(true);
    }

    private syncBodyScrollLock() {
        this.setBodyScrollLocked(Boolean(this.showFilters && this.isMobileViewport));
    }

    private setBodyScrollLocked(locked: boolean) {
        if (typeof document === 'undefined') return;
        if (locked === this.bodyScrollLocked) return;
        this.bodyScrollLocked = locked;
        document.body.style.overflow = locked ? 'hidden' : '';
    }

    private updateStickyTopOffset(): void {
        if (typeof document === 'undefined') {
            this.stickyTopOffsetPx = 0;
            return;
        }

        if (!this.stickyTopOffsetElement?.isConnected) {
            const selectors = ['app-public-header header', 'app-dashboard-header header', 'header.sticky.top-0'];
            for (const selector of selectors) {
                const el = document.querySelector(selector);
                if (!(el instanceof HTMLElement)) continue;
                this.stickyTopOffsetElement = el;
                break;
            }
        }

        const el = this.stickyTopOffsetElement;
        if (!(el instanceof HTMLElement)) {
            this.stickyTopOffsetPx = 0;
            return;
        }

        // Use the visible bottom edge so the filter sits flush under a sticky header,
        // and collapses to `top: 0` when the header scrolls away / is hidden.
        const rect = el.getBoundingClientRect();
        const bottom = rect?.bottom ?? 0;
        if (!Number.isFinite(bottom)) {
            this.stickyTopOffsetPx = 0;
            return;
        }

        this.stickyTopOffsetPx = Math.max(0, Math.floor(bottom));
    }

    private updateStickyTriggerPosition(): void {
        if (typeof window === 'undefined') return;
        const sentinel = this.filterSentinelRef?.nativeElement;
        if (!sentinel) return;
        const rect = sentinel.getBoundingClientRect();
        const scrollY = window.scrollY || window.pageYOffset || 0;
        this.stickyTriggerScrollY = rect.top + scrollY;
    }

    private updateFilterStuckState(): void {
        if (typeof window === 'undefined') return;
        if (this.stickyTriggerScrollY === null) this.updateStickyTriggerPosition();
        if (this.stickyTriggerScrollY === null) return;

        const top = this.stickyTopOffsetPx;
        const enterEpsilonPx = 1;
        const exitBufferPx = 48;
        const scrollY = window.scrollY || window.pageYOffset || 0;
        const enterAt = this.stickyTriggerScrollY - top + enterEpsilonPx;
        const exitAt = this.stickyTriggerScrollY - top - exitBufferPx;

        if (!this.isFilterStuck && scrollY >= enterAt) {
            this.isFilterStuck = true;
        } else if (this.isFilterStuck && scrollY <= exitAt) {
            this.isFilterStuck = false;
        }
    }

    private updateFilterCollapsedState(): void {
        if (typeof window === 'undefined') return;
        if (this.showFilters) {
            this.isFilterCollapsed = false;
            return;
        }
        // Collapse when the filter becomes sticky (both desktop + mobile).
        this.isFilterCollapsed = this.isFilterStuck;
    }

    private applyQueryParams(): void {
        const qp = this.route.snapshot?.queryParamMap;
        if (!qp) return;

        const openFilters = (qp.get('openFilters') || '').toString().trim().toLowerCase();
        if (openFilters === '1' || openFilters === 'true' || openFilters === 'yes') {
            this.showFilters = true;
        }

        const country = qp.get('country');
        if (country) this.filters.country = country;

        const search = qp.get('search');
        if (search) this.filters.search = search;

        const location = qp.get('location');
        if (location) this.filters.location = location;

        const projectId = qp.get('project_id') || qp.get('projectId') || qp.get('project');
        if (projectId) this.filters.projectId = projectId;

        const status = qp.get('status');
        if (status) this.filters.status = this.normalizeStatusFilterValue(status);

        const type = qp.get('type');
        if (type) {
            const normalized = type.trim();
            const match = PROPERTY_TYPE_OPTIONS.find((opt) => opt.value.toLowerCase() === normalized.toLowerCase());
            if (match) this.filters.propertyType = match.value as PropertyTypeValue;
        }

        const bedrooms = qp.get('bedrooms');
        if (bedrooms) {
            const n = Number.parseInt(bedrooms, 10);
            if (Number.isFinite(n) && n > 0) this.filters.bedrooms = n;
        }

        const bathrooms = qp.get('bathrooms');
        if (bathrooms) {
            const n = Number.parseInt(bathrooms, 10);
            if (Number.isFinite(n) && n > 0) this.filters.bathrooms = n;
        }

        const minPrice = qp.get('minPrice');
        if (minPrice) {
            const n = Number.parseFloat(minPrice);
            if (Number.isFinite(n) && n >= 0) this.filters.minPrice = n;
        }

        const maxPrice = qp.get('maxPrice');
        if (maxPrice) {
            const n = Number.parseFloat(maxPrice);
            if (Number.isFinite(n) && n >= 0) this.filters.maxPrice = n;
        }

        if (this.filters.location || this.filters.projectId) {
            this.filterTab = 'location';
        }

        this.initialQueryHasFilters = Boolean(
            (country && country.trim())
            || (search && search.trim())
            || (location && location.trim())
            || (projectId && projectId.trim())
            || (status && status.trim())
            || (type && type.trim())
            || (bedrooms && bedrooms.trim())
            || (bathrooms && bathrooms.trim())
            || (minPrice && minPrice.trim())
            || (maxPrice && maxPrice.trim())
        );
    }

    private applyCachedGeo(): void {
        const cached = this.geoService.peekGeoFromIpCache();
        const countryCode = cached?.countryCode?.trim() || '';
        if (countryCode) this.userCountryCode = countryCode;

        const lat = cached?.latitude ?? null;
        const lng = cached?.longitude ?? null;
        if (typeof lat === 'number' && Number.isFinite(lat) && typeof lng === 'number' && Number.isFinite(lng)) {
            this.userGeo = { lat, lng };
        }
    }

    private async ensureUserGeo(): Promise<void> {
        if (this.userGeo) return;
        if (this.geoPromise) return this.geoPromise;
        this.geoPromise = this.geoService
            .getGeoFromIp()
            .then((geo) => {
                this.userCountryCode = geo?.countryCode?.trim() || this.userCountryCode;
                if (geo?.latitude !== null && geo?.latitude !== undefined && geo?.longitude !== null && geo?.longitude !== undefined) {
                    const lat = Number(geo.latitude);
                    const lng = Number(geo.longitude);
                    if (Number.isFinite(lat) && Number.isFinite(lng)) {
                        this.userGeo = { lat, lng };
                    }
                }
            })
            .catch(() => { })
            .finally(() => {
                this.geoPromise = null;
            });
        return this.geoPromise;
    }

    private async loadUserGeoContext(): Promise<void> {
        await this.ensureUserGeo();

        if (this.properties.length) this.applyLocalFilters(this.properties);
    }

    private async maybeNotifyNoPropertiesInCountry(): Promise<void> {
        if (this.notifiedNoPropertiesInCountry) return;
        if (this.initialQueryHasFilters) return;
        if (String(this.filters.projectId || '').trim()) return;
        if (String(this.filters.country || '').trim()) return;

        await this.ensureUserGeo();
        const countryCode = String(this.userCountryCode || '').trim().toUpperCase();
        if (!countryCode) return;

        try {
            const { items } = await firstValueFrom(
                this.propertiesService.getProperties({ country: countryCode }, { page: 1, perPage: 1 })
            );
            if (Array.isArray(items) && items.length) return;
            this.notifiedNoPropertiesInCountry = true;
            this.notifications
                .showWarningWithTimeout('No properties available in your country yet. Browse other countries from the Country filter.', 15000)
                .subscribe();
        } catch {
            // ignore
        }
    }

    private async loadProjectsForFilter(): Promise<void> {
        if (this.projectsLoaded) return;
        if (this.projectsLoadPromise) return this.projectsLoadPromise;

        this.projectsLoading = true;
        this.projectsError = null;

        this.projectsLoadPromise = (async () => {
            const perPage = 50;
            let page = 1;
            let total = 0;
            const all: Project[] = [];

            // Cap to avoid long loops in very large datasets (still plenty for a filter dropdown).
            const maxPages = 10;
            for (let i = 0; i < maxPages; i++) {
                const resp = await firstValueFrom(this.projectsService.getProjects({ page, perPage }));
                const items = Array.isArray(resp?.items) ? resp.items : [];
                total = Number(resp?.total ?? items.length);
                all.push(...items);
                if (!items.length) break;
                if (Number.isFinite(total) && total > 0 && all.length >= total) break;
                if (items.length < perPage) break;
                page += 1;
            }

            const seen = new Set<string>();
            const unique = all.filter((p) => {
                const id = String(p?.id || '').trim();
                if (!id || seen.has(id)) return false;
                seen.add(id);
                return true;
            });
            unique.sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));

            this.projectsForFilter = unique;
            this.projectsLoaded = true;
        })()
            .catch((err) => {
                this.projectsError = err?.message || 'Failed to load projects';
            })
            .finally(() => {
                this.projectsLoading = false;
                this.projectsLoadPromise = null;
            });

        return this.projectsLoadPromise;
    }

    toggleTag(tag: string) {
        if (this.filters.tags.includes(tag)) {
            this.filters.tags = this.filters.tags.filter(t => t !== tag);
        } else {
            this.filters.tags = [...this.filters.tags, tag];
        }
        this.onFiltersChange();
    }

    onFiltersChange() {
        if (this.initialLoad) return;
        this.currentPage = 1;
        if (this.filters.sortBy === 'nearest') {
            void this.ensureUserGeo().then(() => this.fetchPage());
            return;
        }
        this.fetchPage();
    }

    browseOtherCountries(): void {
        this.filters.country = '';
        this.currentPage = 1;
        if (this.initialLoad) {
            this.fetchPage();
            return;
        }
        this.onFiltersChange();
    }

    private applyLocalFilters(source: Property[]) {
        const { minPrice, maxPrice, featureGarden, featureTerrace, featureGroundFloor, sortBy, tags } = this.filters;

        // Public feed requests already apply the server-backed filters (country/status/type/search/etc).
        // Keep the client-side pass for sort order plus the few filters that are intentionally local-only.
        this.filteredProperties = this.filterAndSort(source, {
            minPrice,
            maxPrice,
            bedrooms: null,
            bathrooms: null,
            status: 'any',
            country: '',
            propertyType: 'any',
            featureGarden,
            featureTerrace,
            featureGroundFloor,
            searchTerm: '',
            sortBy,
            tags
        });
        if (this.filters.sortBy !== 'nearest' && !this.filters.country && this.userCountryCode) {
            this.filteredProperties = this.prioritizeCountryFirst(this.filteredProperties, this.userCountryCode);
        }
    }

    private prioritizeCountryFirst(items: Property[], countryCode: string): Property[] {
        const target = countryCode.trim().toUpperCase();
        if (!target) return items;

        const matches: Property[] = [];
        const rest: Property[] = [];

        for (const property of items) {
            const code = String(property?.countryCode || property?.country || '').trim().toUpperCase();
            if (code === target) matches.push(property);
            else rest.push(property);
        }

        return [...matches, ...rest];
    }

    private filterAndSort(source: Property[], params: {
        minPrice?: number | null;
        maxPrice?: number | null;
        bedrooms?: number | null;
        bathrooms?: number | null;
        status: string;
        country: string;
        propertyType: PropertyTypeValue;
        featureGarden: boolean;
        featureTerrace: boolean;
        featureGroundFloor: boolean;
        searchTerm: string;
        sortBy: SortKey;
        tags: string[];
    }): Property[] {
        const { minPrice, maxPrice, bedrooms, bathrooms, status, country, propertyType, featureGarden, featureTerrace, featureGroundFloor, searchTerm, sortBy, tags } = params;

        return source
            .filter(p => {
                const priceValue = this.convertPrice(p) ?? 0;
                const matchesMin = minPrice ? priceValue >= minPrice : true;
                const matchesMax = maxPrice ? priceValue <= maxPrice : true;
                const matchesBedrooms = bedrooms ? (p.bedrooms || 0) >= bedrooms : true;
                const matchesBathrooms = bathrooms ? (p.bathrooms || 0) >= bathrooms : true;
                const matchesStatus = this.matchesPublicStatusFilter(status, p);
                const matchesCountry = !country
                    ? true
                    : ((p.countryCode || p.country || '').toString().toUpperCase()) === country.toUpperCase();
                const matchesSearch = searchTerm
                    ? [
                        p.unitNumber,
                        p.unitNumber ? `Unit ${p.unitNumber}` : null,
                        p.title,
                        p.tagLine,
                        p.description,
                        p.projectName,
                        p.phaseName,
                        p.location,
                        p.country,
                        ...this.propertyFeatureSearchTerms(p)
                    ].some(value => (value || '').toString().toLowerCase().includes(searchTerm))
                    : true;
                const matchesTags = tags.length === 0
                    ? true
                    : this.propertyFeatureLabels(p).some(tag => tags.some(t => t.toLowerCase() === tag.toLowerCase()));

                const matchesUnitType = propertyType === 'any'
                    ? true
                    : (p.type || '').toString().toLowerCase() === propertyType.toLowerCase();
                const matchesGarden = featureGarden ? propertyHasGarden(p) : true;
                const matchesTerrace = featureTerrace ? propertyHasTerrace(p) : true;
                const matchesGroundFloor = featureGroundFloor ? this.isGroundFloor(p.floor) : true;

                return matchesMin && matchesMax && matchesBedrooms && matchesBathrooms && matchesStatus && matchesCountry && matchesSearch && matchesTags && matchesUnitType && matchesGarden && matchesTerrace && matchesGroundFloor;
            })
            .sort((a, b) => {
                const statusRank = this.statusSortRank(a) - this.statusSortRank(b);
                if (statusRank !== 0) return statusRank;
                switch (sortBy) {
                    case 'nearest': {
                        const da = a.distanceKm ?? Number.POSITIVE_INFINITY;
                        const db = b.distanceKm ?? Number.POSITIVE_INFINITY;
                        if (da === db) return 0;
                        return da - db;
                    }
                    case 'price-low':
                        return (this.convertPrice(a) || Infinity) - (this.convertPrice(b) || Infinity);
                    case 'price-high':
                        return (this.convertPrice(b) || -Infinity) - (this.convertPrice(a) || -Infinity);
                    case 'ppsqm-low':
                        return (this.pricePerSqm(a) || Infinity) - (this.pricePerSqm(b) || Infinity);
                    case 'ppsqm-high':
                        return (this.pricePerSqm(b) || -Infinity) - (this.pricePerSqm(a) || -Infinity);
                    case 'bedrooms':
                        return (b.bedrooms || 0) - (a.bedrooms || 0);
                    case 'bathrooms':
                        return (b.bathrooms || 0) - (a.bathrooms || 0);
                    case 'unit-type':
                        return (a.type || '').toString().localeCompare((b.type || '').toString());
                    case 'title':
                        return a.title.localeCompare(b.title);
                    case 'recent':
                    default:
                        return this.parseDate(b.updated_at || b.created_at) - this.parseDate(a.updated_at || a.created_at);
                }
            });
    }

    private statusSortRank(property: Property): number {
        const raw = (property?.statusBadge || property?.status || '').toString().trim().toLowerCase();
        if (!raw) return 3;
        if (raw === 'available' || raw.includes('available')) return 0;
        if (raw === 'hold' || raw.includes('hold')) return 1;
        if (raw === 'reserved' || raw.includes('reserved')) return 1;
        if (raw === 'sold' || raw.includes('sold')) return 2;
        return 3;
    }

    private propertyFeatureLabels(property: Property): string[] {
        return propertyFeatureLabels(property);
    }

    private propertyFeatureSearchTerms(property: Property): string[] {
        return propertyFeatureSearchTerms(property);
    }

    loadMore() {
        if (!this.hasNextPage || this.loadingPage) return;
        this.currentPage += 1;
        this.fetchPage();
    }

	    private fetchPage() {
	        const isFirstPage = this.currentPage === 1;
	        if (isFirstPage) {
	            this.loading = true;
            this.tagSet.clear();
            this.availableTags = [];
            this.properties = [];
            this.filteredProperties = [];
	        }
	        this.loadingPage = true;
	        const apiFilters = this.buildApiFilters();
	        this.lastRequestSortBy = this.filters.sortBy;
	        this.lastRequestHadGeo = Boolean(apiFilters?.lat && apiFilters?.lng);
	        const includeProjects = isFirstPage && !this.projectsLoaded;
	        if (includeProjects) {
	            this.projectsLoading = true;
	            this.projectsError = null;
	        }

	        this.propertiesService.getProperties(apiFilters, { page: this.currentPage, perPage: this.pageSize, includeProjects }).pipe(
	            finalize(() => {
	                this.loading = false;
	                this.loadingPage = false;
	                this.maybeRefetchNearest();
	            })
	        ).subscribe({
	            next: ({ items, total, page, perPage, projects }) => {
	                if (includeProjects) {
	                    const incoming = Array.isArray(projects) ? projects : [];
	                    if (incoming.length) {
	                        const seen = new Set<string>();
	                        const unique = incoming.filter((p) => {
	                            const id = String(p?.id || '').trim();
	                            if (!id || seen.has(id)) return false;
	                            seen.add(id);
	                            return true;
	                        });
	                        unique.sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
	                        this.projectsForFilter = unique;
	                        this.projectsLoaded = true;
	                    }
	                    this.projectsLoading = false;
	                    if (!this.projectsLoaded) {
	                        void this.loadProjectsForFilter();
	                    }
	                }

                const existingIds = new Set(this.properties.map((p) => p.id));
                const merged = isFirstPage ? items : [...this.properties, ...items.filter((p) => !existingIds.has(p.id))];
                this.properties = merged;
                this.markReservedOnProperties(merged);
                const safeTotal = Number.isFinite(total) ? total : (page - 1) * perPage + items.length;
                this.addFilterOptions(items);
                const serverHasNext = safeTotal > page * perPage;
                this.hasNextPage = serverHasNext || (items.length >= perPage && this.properties.length < safeTotal);
                this.totalCount = this.normalizeVisibleTotalCount(safeTotal, this.properties.length, this.hasNextPage);
                this.applyLocalFilters(this.properties);
	                this.initialLoad = false;
	            },
	            error: () => {
	                if (includeProjects) {
	                    this.projectsLoading = false;
	                    if (!this.projectsLoaded) {
	                        this.projectsError = 'Failed to load projects';
	                        void this.loadProjectsForFilter();
	                    }
	                }
	                if (isFirstPage) {
	                    this.properties = [];
	                    this.filteredProperties = [];
	                    this.totalCount = 0;
	                }
                this.hasNextPage = false;
                this.initialLoad = false;
            }
        });
    }

    private maybeRefetchNearest(): void {
        if (this.loadingPage) return;
        if (this.filters.sortBy !== 'nearest') return;
        if (!this.userGeo) return;
        if (this.lastRequestSortBy === 'nearest' && this.lastRequestHadGeo) return;
        this.currentPage = 1;
        this.fetchPage();
    }

    resultsSummary(): string {
        const loadedCount = this.properties.length;
        const allLoaded = !this.hasNextPage && loadedCount >= this.totalCount;

        if (allLoaded && !this.hasClientOnlyFiltersActive()) {
            return `Showing ${loadedCount} of ${loadedCount}`;
        }

        const visibleTotal = this.hasNextPage
            ? Math.max(this.totalCount, loadedCount, this.filteredProperties.length)
            : Math.max(this.totalCount, loadedCount, this.filteredProperties.length);
        return `Showing ${this.filteredProperties.length} of ${visibleTotal}`;
    }

    private buildApiFilters() {
        const filter: any = {};
        const statusFilter = this.normalizeStatusFilterValue(this.filters.status);
        if (statusFilter && statusFilter !== 'any') filter.status = statusFilter;
        if (this.filters.location) filter.location = String(this.filters.location || '').trim();
        if (this.filters.projectId) filter.project_id = String(this.filters.projectId || '').trim();
        if (this.filters.country && !filter.project_id) filter.country = this.filters.country;
        if (this.filters.propertyType && this.filters.propertyType !== 'any') filter.type = this.filters.propertyType;
        if (this.filters.bedrooms) filter.bedrooms = this.filters.bedrooms;
        if (this.filters.bathrooms) filter.bathrooms = this.filters.bathrooms;
        if (this.filters.search) {
            const raw = String(this.filters.search || '').trim();
            const normalized = raw.replace(/^unit\\s+/i, '').trim();
            filter.search = normalized || raw;
        }
        if (this.filters.sortBy === 'nearest' && this.userGeo) {
            filter.lat = this.userGeo.lat;
            filter.lng = this.userGeo.lng;
        }
        return filter;
    }

    private normalizeStatusFilterValue(value: unknown): string {
        const normalized = String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
        if (!normalized) return 'any';
        if (normalized === 'available') return 'available';
        if (['reserved', 'reserve', 'hold', 'held', 'reservedhold', 'reservedorhold'].includes(normalized)) return 'reserved';
        if (['sold', 'contracted', 'contract'].includes(normalized)) return 'sold';
        return 'any';
    }

    private matchesPublicStatusFilter(status: string, property: Property): boolean {
        const normalized = this.normalizeStatusFilterValue(status);
        if (normalized === 'any') return true;
        const badge = propertyStatusKey(property.statusBadge || property.status || '');
        if (normalized === 'available') return badge === 'available';
        if (normalized === 'reserved') return badge === 'reserved';
        if (normalized === 'sold') return badge === 'sold' || badge === 'contracted';
        return true;
    }

    private addFilterOptions(items: Property[]) {
        for (const p of items) {
            this.propertyFeatureLabels(p).forEach(tag => {
                if (tag) this.tagSet.add(tag);
            });
        }
        this.availableTags = Array.from(this.tagSet).sort();
    }

    private pricePerSqm(p: Property): number | null {
        const price = this.convertPrice(p);
        const area = p.bua || p.totalArea || p.area || null;
        if (!price || !area || area <= 0) return null;
        return price / area;
    }

    private isGroundFloor(floor: string | null | undefined): boolean {
        if (!floor) return false;
        const v = String(floor).trim().toLowerCase();
        if (v === '0' || v === '0.0') return true;
        if (v.includes('ground')) return true;
        if (v === 'gf' || v.startsWith('gf ')) return true;
        return false;
    }

    private parseDate(value?: string | null): number {
        if (!value) return 0;
        const normalized = value
            .replace(' ', 'T')
            .replace(/:(\d{3})$/, '.$1Z');
        const ts = Date.parse(normalized);
        return Number.isFinite(ts) ? ts : 0;
    }

    private convertPrice(property: Property): number | null {
        if (property?.price === null || property?.price === undefined) return null;
        const fromCode = property.currency || property.currencyInfo?.code || 'USD';
        return this.currencyService.convert(property.price, fromCode);
    }

    private async loadReservedPropertyIds(): Promise<void> {
        try {
            const isAuthenticated = await firstValueFrom(this.store.select(selectIsAuthenticated));
            if (!isAuthenticated) return;
            const reservations = await firstValueFrom(this.reservationsService.getReservations({}, { page: 1, perPage: 100 }));
            for (const r of reservations) {
                if (r.propertyId) {
                    this.reservedPropertyIds.add(String(r.propertyId));
                }
            }
            this.applyLocalFilters(this.properties);
        } catch {
            // Best-effort; do not block page load
        }
    }

    private markReservedOnProperties(items: Property[]): void {
        for (const p of items) {
            if (this.reservedPropertyIds.has(p.id)) {
                p.cardBadge1 = 'Reserved / Hold by me';
            }
        }
    }
}
